/*
 * @Author: yidan.chen
 * @Date: 2025-02-19 14:59:55
 * @Last Modified by: yidan.chen
 * @Last Modified time: 2025-03-19 13:45:41
 */
import React, {
  useMemo,
  useCallback,
  ReactNode,
  useEffect,
  useState
} from 'react'
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  Box,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Typography,
  useTheme,
  SelectProps,
  IconButton
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'

export interface Option {
  id: string
  name: string
}

export interface MultiSelectProps {
  name: string
  label: string
  optionList: Option[]
  allCheckedValue?: string
  error?: boolean
  helperText?: string
  disabled?: boolean
  onChange?: (values: string[]) => void
  value?: string[]
  extra?: Partial<SelectProps>
  defaultValue?: string[]
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  name,
  label,
  optionList,
  extra,
  allCheckedValue,
  error,
  helperText,
  disabled = false,
  onChange,
  defaultValue,
  value = []
}) => {
  const theme = useTheme()
  const [internalValue, setInternalValue] = useState<string[]>(value)

  const options = useMemo(() => {
    if (allCheckedValue) {
      return [{ id: allCheckedValue, name: 'All' }, ...optionList]
    }
    return optionList
  }, [optionList, allCheckedValue])

  useEffect(() => {
    if (!onChange || internalValue.length === 0) return

    const validOptionIds = options.map(opt => opt.id)
    const validSelectedValues = internalValue.filter(v =>
      validOptionIds.includes(v)
    )

    // 避免不必要的更新
    let newValues: string[] | null = null

    if (allCheckedValue) {
      if (internalValue.includes(allCheckedValue)) {
        // 如果选中了"全选"
        if (optionList.length === 0) {
          // 如果没有选项，清空选择
          if (internalValue.length > 0) {
            newValues = []
          }
        } else {
          // 如果有选项，确保选中所有选项
          const expectedValues = [
            allCheckedValue,
            ...optionList.map(opt => opt.id)
          ]
          if (
            internalValue.length !== expectedValues.length ||
            !expectedValues.every(v => internalValue.includes(v))
          ) {
            newValues = expectedValues
          }
        }
      } else {
        // 如果没有选中"全选"
        if (validSelectedValues.length !== internalValue.length) {
          // 如果有无效的选中值，更新选中值
          newValues = validSelectedValues
        } else if (
          validSelectedValues.length === optionList.length &&
          optionList.length > 0
        ) {
          // 如果选中了所有选项，添加"全选"
          newValues = [allCheckedValue, ...validSelectedValues]
        }
      }
    } else {
      // 没有"全选"选项的情况
      if (validSelectedValues.length !== internalValue.length) {
        newValues = validSelectedValues
      }
    }

    // 只在值真正需要更新时才调用 onChange
    if (newValues !== null) {
      onChange(newValues)
      setInternalValue(newValues)
    }
  }, [optionList, internalValue, onChange, allCheckedValue, options])

  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setInternalValue(defaultValue)
    }
  }, [defaultValue])

  const handleChange = useCallback(
    (newValues: string[]) => {
      const oldValues = internalValue
      const targetValue =
        newValues.find(v => !oldValues.includes(v)) ||
        oldValues.find(v => !newValues.includes(v))
      const operation = newValues.includes(targetValue as string)
        ? 'add'
        : 'remove'

      let updatedValues: string[]
      if (allCheckedValue) {
        if (targetValue === allCheckedValue) {
          updatedValues = operation === 'add' ? options.map(o => o.id) : []
        } else {
          if (operation === 'add') {
            updatedValues =
              newValues.length === options.length - 1
                ? [allCheckedValue, ...newValues]
                : newValues
          } else {
            updatedValues = newValues.filter(v => v !== allCheckedValue)
          }
        }
      } else {
        updatedValues = newValues
      }

      onChange?.(updatedValues)
      setInternalValue(updatedValues)
    },
    [internalValue, allCheckedValue, onChange, options]
  )

  const onChangeSelect = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      handleChange(event.target.value as string[])
    },
    [handleChange]
  )

  const handleDeleteChip = useCallback(
    (e: React.MouseEvent, chipValue: string) => {
      e.stopPropagation()
      handleChange(internalValue.filter(v => v !== chipValue))
    },
    [handleChange, internalValue]
  )

  const chipStyles = useCallback(
    (chipValue: string) => {
      const isAllChecked = chipValue === allCheckedValue
      return {
        color: !isAllChecked ? 'white' : theme.palette.text.primary,
        backgroundColor: !isAllChecked
          ? theme.palette.secondary.dark
          : undefined,
        marginRight: 1,
        maxHeight: '24px',
        '& .MuiChip-deleteIcon': {
          width: '16px',
          height: '16px',
          color: !isAllChecked ? 'white' : undefined
        }
      }
    },
    [allCheckedValue, theme.palette]
  )

  const renderValue = useCallback(
    (selected: string[]) => (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          position: 'relative',
          '&:hover .clear-all-button': {
            opacity: 1
          }
        }}>
        {selected.length > 0 && (
          <Chip
            key={selected[0]}
            label={
              <Typography fontSize={13} sx={{ lineHeight: '100%' }}>
                {options.find(option => option.id === selected[0])?.name}
              </Typography>
            }
            onDelete={e => handleDeleteChip(e, selected[0])}
            onMouseDown={e => e.stopPropagation()}
            sx={chipStyles(selected[0])}
          />
        )}
        {selected.length > 1 &&
          !selected.includes(allCheckedValue as string) && (
            <Typography
              sx={{
                color: theme => theme.palette.secondary.dark
              }}
              fontSize={16}
              fontWeight={'bold'}>
              +{selected.length - 1}
            </Typography>
          )}
        {selected.length > 0 && (
          <IconButton
            size='small'
            className='clear-all-button'
            onClick={e => {
              e.stopPropagation()
              handleChange([])
            }}
            onMouseDown={e => {
              e.preventDefault()
              e.stopPropagation()
            }}
            sx={{
              position: 'absolute',
              right: 0,
              opacity: 0,
              transition: 'opacity 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}>
            <ClearIcon fontSize='small' />
          </IconButton>
        )}
      </Box>
    ),
    [options, handleDeleteChip, chipStyles, handleChange]
  )

  return (
    <FormControl fullWidth error={error} disabled={disabled}>
      <InputLabel id={`${name}-label`} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={`${name}-label`}
        id={name}
        multiple
        value={internalValue}
        onChange={
          onChangeSelect as (
            event: SelectChangeEvent<unknown>,
            child: ReactNode
          ) => void
        }
        input={<OutlinedInput notched label={label} />}
        renderValue={renderValue as (value: unknown) => ReactNode}
        sx={{ minWidth: '100%', height: '36px' }}
        name={name}
        aria-label={label}
        {...extra}>
        {options.map(option => (
          <MenuItem
            key={option.id}
            value={option.id}
            sx={{
              padding: 0,
              height: '36px'
            }}>
            <Checkbox
              checked={internalValue.includes(option.id)}
              aria-label={`Select ${option.name}`}
            />
            <ListItemText primary={option.name} />
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <Typography color={error ? 'error' : 'textSecondary'} variant='caption'>
          {helperText}
        </Typography>
      )}
    </FormControl>
  )
}

export default MultiSelect
